// 批量折扣价格获取器 - 单独请求版
window.BatchDiscountPriceFetcher = (function() {
  const DEBUG_MODE = false;
  const REQUEST_DELAY = 100; // 每个请求之间的延迟
  const MAX_CONCURRENT = 4; // 最大并发请求数
  
  // 获取配置
  const STOREFRONT_TOKEN = window.STOREFRONT_ACCESS_TOKEN || window.ShopifyConfig?.storefrontAccessToken || '';
  const SHOP_DOMAIN = window.Shopify?.shop || window.location.hostname;
  
  // 缓存管理
  const priceCache = new Map();
  const CACHE_DURATION = 10 * 60 * 1000;
  
  // 请求队列管理
  const requestQueue = [];
  let activeRequests = 0;
  let isProcessing = false;
  
  // 已处理的元素
  const processedElements = new WeakSet();
  
  function debugLog(message, data = null) {
    if (DEBUG_MODE) {
      console.log(`[BatchDiscountPriceFetcher] ${message}`, data || '');
    }
  }

  // 获取单个变体的折扣价格
  async function fetchSingleVariantPrice(variantId) {
    debugLog('获取单个变体价格', { variantId });

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            lines(first: 1) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      product {
                        title
                        handle
                      }
                    }
                  }
                  cost {
                    totalAmount {
                      amount
                      currencyCode
                    }
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartAutomaticDiscountAllocation {
                      title
                      discountedAmount {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        lines: [{
          merchandiseId: variantId.startsWith('gid://') ? variantId : `gid://shopify/ProductVariant/${variantId}`,
          quantity: 1
        }]
      }
    };

    try {
      const response = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
        },
        body: JSON.stringify({ query, variables })
      });

      const result = await response.json();
      
      if (result.data?.cartCreate?.cart?.lines?.edges?.[0]) {
        const line = result.data.cartCreate.cart.lines.edges[0].node;
        
        const originalPrice = parseFloat(line.merchandise.price.amount);
        const compareAtPrice = line.merchandise.compareAtPrice 
          ? parseFloat(line.merchandise.compareAtPrice.amount) 
          : originalPrice;
        const finalPrice = parseFloat(line.cost.totalAmount.amount) / line.quantity;
        const hasDiscount = line.discountAllocations.length > 0;
        
        const discountFromCompareAt = compareAtPrice > finalPrice ? compareAtPrice - finalPrice : 0;
        const discountPercentage = compareAtPrice > finalPrice 
          ? Math.round((discountFromCompareAt / compareAtPrice) * 100) 
          : 0;
        
        const priceData = {
          variantId: line.merchandise.id,
          originalPrice: originalPrice,
          compareAtPrice: compareAtPrice,
          finalPrice: finalPrice,
          discountAmount: originalPrice - finalPrice,
          discountFromCompareAt: discountFromCompareAt,
          discountPercentage: discountPercentage,
          hasDiscount: hasDiscount || compareAtPrice > finalPrice,
          discountTitle: line.discountAllocations[0]?.title || '',
          currencyCode: line.cost.totalAmount.currencyCode,
          productTitle: line.merchandise.product?.title || '',
          productHandle: line.merchandise.product?.handle || ''
        };
        
        // 缓存结果
        const shortId = variantId.replace('gid://shopify/ProductVariant/', '');
        priceCache.set(variantId, {
          data: priceData,
          timestamp: Date.now()
        });
        priceCache.set(shortId, {
          data: priceData,
          timestamp: Date.now()
        });
        priceCache.set(line.merchandise.id, {
          data: priceData,
          timestamp: Date.now()
        });
        
        return priceData;
      }
    } catch (error) {
      debugLog('查询错误', { variantId, error });
    }
    
    return null;
  }

  // 处理请求队列
  async function processQueue() {
    if (isProcessing || requestQueue.length === 0) return;
    
    isProcessing = true;
    
    while (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT) {
      const request = requestQueue.shift();
      if (!request) continue;
      
      activeRequests++;
      
      // 异步处理请求
      (async () => {
        const { variantId, element, compareAtPrice } = request;
        
        try {
          // 检查缓存
          const cached = priceCache.get(variantId);
          if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
            debugLog('使用缓存数据', { variantId });
            const priceData = { ...cached.data };
            
            // 如果有本地 compare_at_price，使用它
            if (compareAtPrice && (!priceData.compareAtPrice || priceData.compareAtPrice < compareAtPrice)) {
              priceData.compareAtPrice = compareAtPrice;
              priceData.discountFromCompareAt = compareAtPrice - priceData.finalPrice;
              priceData.discountPercentage = compareAtPrice > priceData.finalPrice 
                ? Math.round(((compareAtPrice - priceData.finalPrice) / compareAtPrice) * 100) 
                : 0;
              priceData.hasDiscount = priceData.hasDiscount || compareAtPrice > priceData.finalPrice;
            }
            
            updatePriceDisplay(element, priceData);
            processedElements.add(element);
          } else {
            // 获取新数据
            const priceData = await fetchSingleVariantPrice(variantId);
            
            if (priceData) {
              // 如果元素有 compare_at_price，使用它来计算折扣
              if (compareAtPrice && (!priceData.compareAtPrice || priceData.compareAtPrice < compareAtPrice)) {
                priceData.compareAtPrice = compareAtPrice;
                priceData.discountFromCompareAt = compareAtPrice - priceData.finalPrice;
                priceData.discountPercentage = compareAtPrice > priceData.finalPrice 
                  ? Math.round(((compareAtPrice - priceData.finalPrice) / compareAtPrice) * 100) 
                  : 0;
                priceData.hasDiscount = priceData.hasDiscount || compareAtPrice > priceData.finalPrice;
              }
              
              updatePriceDisplay(element, priceData);
            }
            
            processedElements.add(element);
          }
        } finally {
          activeRequests--;
          
          // 添加延迟
          if (requestQueue.length > 0) {
            await new Promise(resolve => setTimeout(resolve, REQUEST_DELAY));
          }
        }
      })();
    }
    
    isProcessing = false;
    
    // 如果还有请求，继续处理
    if (requestQueue.length > 0) {
      setTimeout(() => processQueue(), 100);
    }
  }

  // 添加产品到请求队列
  function queueProduct(variantId, element) {
    // 跳过已处理的元素
    if (processedElements.has(element)) {
      return;
    }

    // 从元素中获取 compare_at_price
    const compareAtPriceText = element.querySelector('.product-card__compare-price')?.textContent;
    const compareAtPrice = compareAtPriceText ? parseFloat(compareAtPriceText.replace(/[^0-9.]/g, '')) : null;
    
    debugLog('添加到队列', {
      variantId: variantId,
      compareAtPrice: compareAtPrice,
      productTitle: element.closest('.product-card')?.querySelector('.product-card__title')?.textContent
    });

    // 添加到队列
    requestQueue.push({
      variantId,
      element,
      compareAtPrice
    });
    
    // 开始处理队列
    processQueue();
  }

  // 更新价格显示
  function updatePriceDisplay(element, priceData) {
    debugLog('更新价格显示', {
      product: priceData.productTitle,
      originalPrice: priceData.originalPrice,
      compareAtPrice: priceData.compareAtPrice,
      finalPrice: priceData.finalPrice,
      discountPercentage: priceData.discountPercentage
    });
    
    // 只在有折扣时更新
    if (priceData.hasDiscount && priceData.discountPercentage > 0) {
      const priceHtml = `
        <span class="product-card__sale-price product-card__sale-price--discount">
          ${formatMoney(priceData.finalPrice, priceData.currencyCode)}
        </span>
        <s class="product-card__compare-price">
          ${formatMoney(priceData.compareAtPrice || priceData.originalPrice, priceData.currencyCode)}
        </s>
      `;

      // 查找价格包装器，只更新价格部分
      const priceWrapper = element.querySelector('.product-card__price-wrapper');
      if (priceWrapper) {
        priceWrapper.innerHTML = priceHtml;
      } else {
        // 如果没有包装器，更新整个元素（保留 from 文本和单位价格）
        const fromText = element.querySelector('.price-from');
        const unitPrice = element.querySelector('.product-card__unit-price');
        
        let newContent = '';
        if (fromText) {
          newContent += fromText.outerHTML;
        }
        newContent += `<span class="product-card__price-wrapper">${priceHtml}</span>`;
        if (unitPrice) {
          newContent += unitPrice.outerHTML;
        }
        
        element.innerHTML = newContent;
      }
      
      element.classList.add('has-discount');
    }
  }

  // 格式化金额
  function formatMoney(amount, currencyCode) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount * 100, window.Shopify.money_format);
    }
    
    return new Intl.NumberFormat(window.Shopify?.locale || 'en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(amount);
  }

  // 处理所有产品元素
  function processAllProducts() {
    debugLog('处理所有产品');
    
    const priceElements = document.querySelectorAll('.product-card__price[data-product-variant-id]');
    debugLog(`找到 ${priceElements.length} 个产品`);
    
    if (priceElements.length === 0) {
      debugLog('未找到产品价格元素');
      return;
    }

    // 将所有产品添加到队列
    priceElements.forEach((element) => {
      const variantId = element.dataset.productVariantId;
      if (variantId && !processedElements.has(element)) {
        const cleanId = variantId.replace('gid://shopify/ProductVariant/', '');
        queueProduct(cleanId, element);
      }
    });
  }

  // 初始化
  function init() {
    if (!STOREFRONT_TOKEN) {
      console.error('[BatchDiscountPriceFetcher] Storefront Access Token 未配置');
      return;
    }

    debugLog('初始化', {
      token: STOREFRONT_TOKEN.substring(0, 10) + '...',
      shop: SHOP_DOMAIN
    });

    // 延迟处理，确保页面加载完成
    setTimeout(processAllProducts, 500);
    
    // 监听动态内容加载
    const observer = new MutationObserver((mutations) => {
      let hasNewProducts = false;
      
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) {
            if (node.querySelector?.('.product-card__price[data-product-variant-id]') ||
                node.matches?.('.product-card__price[data-product-variant-id]')) {
              hasNewProducts = true;
            }
          }
        });
      });
      
      if (hasNewProducts) {
        debugLog('检测到新产品');
        setTimeout(processAllProducts, 200);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  return {
    init: init,
    refresh: () => {
      // 清除已处理标记但保留缓存
      processedElements.clear();
      requestQueue.length = 0;
      activeRequests = 0;
      isProcessing = false;
      processAllProducts();
    },
    clearCache: () => {
      priceCache.clear();
      debugLog('缓存已清空');
    },
    getCache: () => priceCache,
    processAll: processAllProducts,
    getQueueStatus: () => ({
      pending: requestQueue.length,
      active: activeRequests,
      processing: isProcessing
    })
  };
})();

// DOM 加载完成后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    BatchDiscountPriceFetcher.init();
  });
} else {
  BatchDiscountPriceFetcher.init();
}

// 支持动态加载的产品
document.addEventListener('products:loaded', function() {
  setTimeout(() => BatchDiscountPriceFetcher.processAll(), 100);
});

// 支持 AJAX 分页
document.addEventListener('ajaxCart:updated', function() {
  BatchDiscountPriceFetcher.processAll();
});

// 控制台快捷命令
window.bdf = {
  init: BatchDiscountPriceFetcher.init,
  refresh: BatchDiscountPriceFetcher.refresh,
  clearCache: BatchDiscountPriceFetcher.clearCache,
  processAll: BatchDiscountPriceFetcher.processAll,
  status: BatchDiscountPriceFetcher.getQueueStatus
};

console.log('💰 BatchDiscountPriceFetcher 已加载！使用 bdf.refresh() 刷新所有价格，bdf.status() 查看队列状态');